import React, { useState, useEffect } from "react";
import { FaBriefcase, FaMapMarkerAlt, FaCalendarAlt } from "react-icons/fa";

export const DEFAULT_EXPERIENCES = [
  {
    id: 3,
    side: "right",
    company: "Tim Digital",
    role: "Web Developer",
    location: "Remote / On-site",
    date: "Present",
    type: "Full Time",
    points: [
      "Engineered responsive web applications and interactive client features using modern web technologies.",
      "Collaborated with cross-functional teams to build clean, high-performance UI layouts and integrated APIs.",
    ],
  },
  {
    id: 1,
    side: "left",
    company: "AlmaBetter",
    role: "Web Development Trainee",
    location: "Bangalore, Karnataka",
    date: "Apr 2025 – Oct 2025",
    type: "Full Stack Training",
    points: [
      "Engineered and launched 3+ full-stack MERN applications with responsive UI, JWT authentication, and role-based access.",
      "Built 20+ reusable React.js components using Tailwind CSS, reducing recurring UI defects by 30%.",
      "Developed a complete movie booking platform using React.js and Node.js, handling 500+ monthly simulated transactions with Redux state management.",
    ],
  },
  {
    id: 2,
    side: "right",
    company: "Euphoria Genx",
    role: "Software Engineering Intern",
    location: "Kolkata, WB",
    date: "Jun 2023 – Nov 2023",
    type: "Internship",
    points: [
      "Developed 20+ responsive UI components in Laravel using Tailwind CSS, reducing layout bugs by 30% across modules.",
      "Integrated 10+ REST APIs with Sanctum authentication, handling 1,000+ secure sessions/month with <0.5% failure rate.",
      "Improved frontend performance by 25% and increased user engagement by 15% across key features.",
    ],
  },
];

export const getExperiencesList = () => {
  const stored = localStorage.getItem("custom_experiences");
  if (stored) {
    try {
      const storedExp = JSON.parse(stored);
      const missingInitial = DEFAULT_EXPERIENCES.filter(
        (initItem) => !storedExp.some((e) => e.id === initItem.id || e.company.toLowerCase() === initItem.company.toLowerCase())
      );
      if (missingInitial.length > 0) {
        const merged = [...missingInitial, ...storedExp];
        localStorage.setItem("custom_experiences", JSON.stringify(merged));
        return merged;
      }
      return storedExp;
    } catch (e) {
      return DEFAULT_EXPERIENCES;
    }
  }
  return DEFAULT_EXPERIENCES;
};

const Experience = () => {
  const [experiencesList, setExperiencesList] = useState(getExperiencesList());

  useEffect(() => {
    const handleStorage = () => {
      setExperiencesList(getExperiencesList());
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  return (
    <section
      name="experience"
      className="relative w-full bg-[#0F172A] text-slate-100 pt-8 pb-16 overflow-hidden"
    >
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[42rem] h-[42rem] bg-blue-600/8 rounded-full blur-[150px] pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-[30rem] h-[30rem] bg-[#00B4DB]/12 rounded-full blur-[140px] pointer-events-none" />

      {/* Section Header */}
      <div className="text-center max-w-3xl mx-auto px-6 mb-12 relative z-10">
        <span className="text-[#00B4DB] text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full bg-[#00B4DB]/10 border border-[#00B4DB]/30">
          Career Journey
        </span>
        <h2 className="text-3xl sm:text-5xl font-extrabold text-white mt-4 tracking-tight">
          Work <span className="gradient-text">Experience</span>
        </h2>
        <p className="text-slate-400 text-sm sm:text-base mt-3">
          Practical industry experience building full-stack applications and frontend interfaces.
        </p>
      </div>

      <div className="relative max-w-6xl mx-auto px-6 z-10">
        {/* Timeline line */}
        <div className="absolute left-6 md:left-1/2 top-0 h-full w-[2px] bg-gradient-to-b from-blue-600 via-[#00B4DB] to-cyan-500 -translate-x-1/2 shadow-sm" />

        <div className="flex flex-col gap-12 sm:gap-16">
          {experiencesList.map((exp, index) => {
            const side = exp.side || (index % 2 === 0 ? "right" : "left");
            return (
              <div
                key={exp.id || index}
                className={`relative flex w-full ${
                  side === "left" ? "md:justify-start" : "md:justify-end"
                }`}
              >
                {/* Timeline Badge Icon */}
                <span
                  className="absolute left-6 md:left-1/2 top-6 w-9 h-9
                             bg-[#0F172A] border-2 border-[#00B4DB] rounded-full
                             -translate-x-1/2 flex items-center justify-center
                             shadow-md z-20"
                >
                  <FaBriefcase className="text-[#00B4DB] text-xs" />
                </span>

                {/* Card Container */}
                <div
                  className="ml-10 sm:ml-12 md:ml-0 w-[calc(100%-2.5rem)] sm:w-[calc(100%-3rem)] md:w-[45%]
                             bg-[#1E293B]/85 border border-slate-700/80 rounded-2xl p-6 sm:p-8
                             shadow-xl hover:border-[#00B4DB]/60 transition-all duration-300 hover:-translate-y-1 group"
                >
                  {/* Header info */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <h3 className="text-xl sm:text-2xl font-bold text-white group-hover:text-[#00B4DB] transition-colors">
                        {exp.company}
                      </h3>
                      <p className="text-sm font-semibold text-[#00B4DB] mt-0.5">
                        {exp.role}
                      </p>
                    </div>
                    {exp.type && (
                      <span className="text-xs px-2.5 py-1 rounded-md bg-[#00B4DB]/10 border border-[#00B4DB]/30 text-[#00B4DB] font-semibold flex-shrink-0 whitespace-nowrap">
                        {exp.type}
                      </span>
                    )}
                  </div>

                  {/* Sub details */}
                  <div className="flex items-center gap-4 text-xs text-slate-400 mb-6 flex-wrap">
                    <span className="flex items-center gap-1.5">
                      <FaMapMarkerAlt className="text-[#00B4DB]" /> {exp.location}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <FaCalendarAlt className="text-sky-400" /> {exp.date}
                    </span>
                  </div>

                  {/* Bullet points */}
                  <ul className="space-y-3 text-xs sm:text-sm text-slate-300">
                    {exp.points.map((point, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#00B4DB] mt-2 flex-shrink-0" />
                        <span className="leading-relaxed">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Experience;
